import { useEffect, useMemo, useState } from 'react';
import { ProviderLayout } from '@/components/ProviderLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useProviderAuth } from '@/contexts/ProviderAuthContext';
import {
  createProviderRequest,
  getProviderRequests,
  getProviderPatients,
  getProviderPatientRecords,
  runProviderAnalysis
} from '@/services/api';
import { toast } from 'sonner';
import { ClipboardList, Sparkles, UserCheck } from 'lucide-react';

export default function ProviderDashboardPage() {
  const { provider } = useProviderAuth();
  const [requestForm, setRequestForm] = useState({
    patient_identifier: '',
    reason: '',
    requested_record_types: '',
    notes: ''
  });
  const [requests, setRequests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patientRecords, setPatientRecords] = useState([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [symptoms, setSymptoms] = useState('');
  const [deviceData, setDeviceData] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProviderData();
  }, []);

  async function loadProviderData() {
    try {
      const [requestData, patientData] = await Promise.all([
        getProviderRequests(),
        getProviderPatients()
      ]);
      setRequests(requestData);
      setPatients(patientData);
    } catch (error) {
      toast.error('Unable to load provider data');
    }
  }

  useEffect(() => {
    if (!selectedPatient) {
      setPatientRecords([]);
      setSelectedRecordIds([]);
      return;
    }

    const loadRecords = async () => {
      try {
        const records = await getProviderPatientRecords(selectedPatient);
        setPatientRecords(records);
      } catch (error) {
        toast.error('Unable to load patient records');
      }
    };

    loadRecords();
  }, [selectedPatient]);

  const approvedRequests = useMemo(() => requests.filter((req) => req.status === 'approved'), [requests]);

  const handleRequestChange = (field) => (event) => {
    setRequestForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreateRequest = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const recordTypes = requestForm.requested_record_types
        ? requestForm.requested_record_types.split(',').map((item) => item.trim()).filter(Boolean)
        : [];
      await createProviderRequest({
        patient_identifier: requestForm.patient_identifier,
        reason: requestForm.reason,
        requested_record_types: recordTypes,
        notes: requestForm.notes
      });
      toast.success('Request sent to patient');
      setRequestForm({ patient_identifier: '', reason: '', requested_record_types: '', notes: '' });
      loadProviderData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecordSelection = (recordId) => {
    setSelectedRecordIds((prev) =>
      prev.includes(recordId) ? prev.filter((id) => id !== recordId) : [...prev, recordId]
    );
  };

  const handleRunAnalysis = async () => {
    if (!selectedPatient) {
      toast.error('Select a patient');
      return;
    }
    setLoading(true);
    try {
      const symptomList = symptoms
        ? symptoms.split(',').map((item) => item.trim()).filter(Boolean)
        : [];
      const result = await runProviderAnalysis({
        patient_id: selectedPatient,
        record_ids: selectedRecordIds.length ? selectedRecordIds : null,
        symptoms: symptomList,
        device_data: deviceData
      });
      setAnalysis(result);
      toast.success('Analysis generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProviderLayout>
      <div className="space-y-6" data-testid="provider-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome, {provider?.full_name || 'Provider'}
          </h1>
          <p className="text-slate-500">Manage patient requests, summaries, and discrepancy checks.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-white border-slate-100 shadow-sm" data-testid="provider-request-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-blue-900" />Request Patient Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRequest} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="patientIdentifier">Patient Email or Mobile</Label>
                  <Input
                    id="patientIdentifier"
                    value={requestForm.patient_identifier}
                    onChange={handleRequestChange('patient_identifier')}
                    required
                    data-testid="provider-request-patient-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requestReason">Reason for Request</Label>
                  <Input
                    id="requestReason"
                    value={requestForm.reason}
                    onChange={handleRequestChange('reason')}
                    required
                    data-testid="provider-request-reason-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recordTypes">Requested Record Types (comma separated)</Label>
                  <Input
                    id="recordTypes"
                    value={requestForm.requested_record_types}
                    onChange={handleRequestChange('requested_record_types')}
                    placeholder="blood_test, imaging, checkup"
                    data-testid="provider-request-record-types-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requestNotes">Notes</Label>
                  <Textarea
                    id="requestNotes"
                    value={requestForm.notes}
                    onChange={handleRequestChange('notes')}
                    rows={3}
                    data-testid="provider-request-notes-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  disabled={loading}
                  data-testid="provider-request-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Request'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-sm" data-testid="provider-requests-list">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-blue-900" />Request Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {requests.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="provider-no-requests">No requests yet.</p>
                )}
                {requests.map((request) => (
                  <div key={request.id} className="border border-slate-200 rounded-lg p-3" data-testid={`provider-request-${request.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{request.patient_identifier}</p>
                        <p className="text-xs text-slate-500">{request.reason}</p>
                      </div>
                      <Badge variant="secondary" data-testid={`provider-request-status-${request.id}`}>
                        {request.status}
                      </Badge>
                    </div>
                    {request.requested_record_types?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-2">Types: {request.requested_record_types.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white border-slate-100 shadow-sm" data-testid="provider-analysis-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-900" />AI Summary & Discrepancy Detector
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Approved Patient</Label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger data-testid="provider-patient-select">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id} data-testid={`provider-patient-${patient.id}`}>
                        {patient.full_name} ({patient.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Approved Requests</Label>
                <div className="text-sm text-slate-500" data-testid="provider-approved-count">
                  {approvedRequests.length} approved request(s)
                </div>
              </div>
            </div>

            {selectedPatient && (
              <div className="border border-slate-200 rounded-lg p-4" data-testid="provider-record-selection">
                <p className="text-sm font-medium text-slate-700 mb-2">Include Records</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {patientRecords.length === 0 && (
                    <p className="text-sm text-slate-500" data-testid="provider-no-records">No records available.</p>
                  )}
                  {patientRecords.map((record) => (
                    <label key={record.id} className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={selectedRecordIds.includes(record.id)}
                        onChange={() => toggleRecordSelection(record.id)}
                        data-testid={`provider-record-checkbox-${record.id}`}
                      />
                      {record.title} ({record.record_type})
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="symptoms">Symptoms (comma separated)</Label>
                <Textarea
                  id="symptoms"
                  rows={3}
                  value={symptoms}
                  onChange={(event) => setSymptoms(event.target.value)}
                  placeholder="fatigue, chest pain, shortness of breath"
                  data-testid="provider-symptoms-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deviceData">Device Data (optional)</Label>
                <Textarea
                  id="deviceData"
                  rows={3}
                  value={deviceData}
                  onChange={(event) => setDeviceData(event.target.value)}
                  placeholder="Wearable trends, BP readings, glucose, etc."
                  data-testid="provider-device-data-input"
                />
              </div>
            </div>

            <Button
              className="bg-blue-900 hover:bg-blue-800 text-white"
              onClick={handleRunAnalysis}
              disabled={loading}
              data-testid="provider-run-analysis-btn"
            >
              {loading ? 'Generating...' : 'Generate Summary'}
            </Button>

            {analysis && (
              <div className="mt-4 border-t border-slate-200 pt-4 space-y-4" data-testid="provider-analysis-result">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Summary</p>
                  <p className="text-sm text-slate-600" data-testid="provider-analysis-summary">{analysis.summary}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Discrepancies</p>
                  <ul className="list-disc list-inside text-sm text-slate-600" data-testid="provider-analysis-discrepancies">
                    {analysis.discrepancies?.length ? analysis.discrepancies.map((item, index) => (
                      <li key={index}>{item}</li>
                    )) : <li>No discrepancies flagged.</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Risk Flags</p>
                  <ul className="list-disc list-inside text-sm text-slate-600" data-testid="provider-analysis-risks">
                    {analysis.risk_flags?.length ? analysis.risk_flags.map((item, index) => (
                      <li key={index}>{item}</li>
                    )) : <li>No urgent risks flagged.</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Follow-up Questions</p>
                  <ul className="list-disc list-inside text-sm text-slate-600" data-testid="provider-analysis-questions">
                    {analysis.follow_up_questions?.length ? analysis.follow_up_questions.map((item, index) => (
                      <li key={index}>{item}</li>
                    )) : <li>No follow-up questions generated.</li>}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-100 shadow-sm" data-testid="provider-aiims-card">
          <CardHeader>
            <CardTitle>AIIMS CDSS (Pending Credentials)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-500">
              AIIMS CDSS integration will provide decision support based on trends and symptom inputs once API access is configured.
            </p>
            <Button variant="outline" disabled data-testid="provider-aiims-disabled-btn">
              Awaiting Credentials
            </Button>
          </CardContent>
        </Card>
      </div>
    </ProviderLayout>
  );
}